import React, { useEffect, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useGamePortalPagesState } from 'state/hooks/game-portal-pages.hook';
import { z } from 'zod';

import EmptyState from 'components/EmptyState/EmptyState';

import { IBlock } from '../../constants';
import { pageFormSchema } from '../../validation-schema';

import { BlockMenuItem } from './BlockMenuItem';
import { SortableBlockItem } from './SortableBlockItem';

export const BlocksList: React.FC = () => {
  const { watch, setValue } = useFormContext<z.infer<typeof pageFormSchema>>();
  const { openBlockIndex, setOpenBlockIndex } = useGamePortalPagesState();

  const formBlocks = watch('blocksV2') as IBlock[];

  const [blocks, setBlocks] = useState<IBlock[]>(formBlocks || []);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeBlock, setActiveBlock] = useState<IBlock | null>(null);

  useEffect(() => {
    if (formBlocks) {
      setBlocks(formBlocks);
    }
  }, [formBlocks]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    const index = parseInt(id, 10);
    setActiveId(id);
    setActiveBlock(blocks[index]);
    setOpenBlockIndex(undefined);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      setActiveBlock(null);
      return;
    }

    setBlocks((currentBlocks: IBlock[]) => {
      const oldIndex = currentBlocks.findIndex(
        (block: IBlock, index: number) => String(index) === String(active.id)
      );
      const newIndex = currentBlocks.findIndex(
        (block: IBlock, index: number) => String(index) === String(over.id)
      );

      const newBlocks = arrayMove(currentBlocks, oldIndex, newIndex);

      setValue('blocksV2', newBlocks, {
        shouldValidate: true,
        shouldDirty: true
      });

      if (openBlockIndex === undefined) {
        return newBlocks;
      }

      // Update the open block index if it was affected by the reorder
      if (openBlockIndex === oldIndex) {
        // The open block was moved, update to new position
        setOpenBlockIndex(newIndex);
      } else if (oldIndex < openBlockIndex && openBlockIndex <= newIndex) {
        // Block was moved forward, shift open index back
        setOpenBlockIndex(openBlockIndex - 1);
      } else if (newIndex < openBlockIndex && openBlockIndex < oldIndex) {
        // Block was moved backward, shift open index forward
        setOpenBlockIndex(openBlockIndex + 1);
      }

      return newBlocks;
    });

    setActiveId(null);
    setActiveBlock(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveBlock(null);
  };

  const handleDelete = (index: number) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    // Update local state immediately
    setBlocks(newBlocks);
    // Update form value
    setValue('blocksV2', newBlocks, {
      shouldValidate: true,
      shouldDirty: true
    });
    // Close the block if it was open, or adjust the open index if needed
    if (openBlockIndex === index) {
      setOpenBlockIndex(undefined);
    } else if (openBlockIndex !== undefined && openBlockIndex > index) {
      // If a block before the open one was deleted, shift the index
      setOpenBlockIndex(openBlockIndex - 1);
    }
  };

  const handleToggleExpand = (index: number) => {
    if (openBlockIndex === index) {
      setOpenBlockIndex(undefined);
    } else {
      setOpenBlockIndex(index);
    }
  };

  const items = blocks.map((_, index) => String(index));

  if (blocks.length === 0) {
    return (
      <EmptyState
        title="Start Adding your Blocks"
        description="After your block is created, it will be listed here."
        icon="fa-regular fa-cube"
        onClick={() => {}}
        hideButton={true}
        className=""
      />
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2">
          {blocks.map((block, index) => {
            const isDragging = activeId === String(index);
            const isExpanded = openBlockIndex === index;

            return (
              <SortableBlockItem key={index} id={String(index)}>
                <BlockMenuItem
                  block={block}
                  index={index}
                  isExpanded={isExpanded}
                  isDragging={activeId !== null}
                  isCurrentItemDragged={isDragging}
                  onDelete={() => handleDelete(index)}
                  onToggleExpand={() => handleToggleExpand(index)}
                />
              </SortableBlockItem>
            );
          })}
        </div>
      </SortableContext>
      <DragOverlay>
        {activeId && activeBlock ? (
          <div className="cursor-grabbing shadow-lg bg-white z-50">
            <BlockMenuItem
              block={activeBlock}
              index={parseInt(activeId, 10)}
              isExpanded={false}
              isDragging={true}
              isCurrentItemDragged={true}
              onDelete={() => {}}
              onToggleExpand={() => {}}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
